import { handleTokenExpiration, isTokenInvalid } from 'lib-browser';
import { getToken } from '../../util/getToken';

/* eslint-disable @typescript-eslint/no-explicit-any */
interface AuthorizedFetchParams {
  method: string;
  body?: string;
}

export type AuthorizedFetchWithBaseUrl = (
  path: string,
  params: AuthorizedFetchParams,
  headers?: HeadersInit,
) => Promise<Blob | string | any>;

export type AuthorizedFetch = (baseUrl: Promise<string> | string) => AuthorizedFetchWithBaseUrl;

export const authorizedFetchRaw: AuthorizedFetch =
  (baseUrl) =>
  async (path: string, params: AuthorizedFetchParams, headers?: HeadersInit): Promise<Response> => {
    const url = typeof baseUrl === 'string' ? baseUrl : await baseUrl;
    const token = getToken();

    if (!token) {
      throw new Error('Not authorized. Please refresh page.');
    }

    const res = await fetch(`${url}/${path}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...headers,
      },
      ...params,
    });

    if (isTokenInvalid(res)) {
      handleTokenExpiration();
      return res;
    }

    if (!res.ok) {
      throw new Error(res.statusText);
    } else {
      return res;
    }
  };

export const authorizedFetch: AuthorizedFetch = (baseUrl) => {
  const fetch = authorizedFetchRaw(baseUrl);
  return async (
    path: string,
    params: AuthorizedFetchParams,
    headers?: HeadersInit,
  ): Promise<Blob | string | unknown> => {
    const res = await fetch(path, params, headers);

    if (isTokenInvalid(res)) {
      handleTokenExpiration();
      return res;
    }

    const contentType = res.headers.get('Content-type');
    if (contentType && contentType.includes('application/json')) {
      return res.json();
    } else if (
      contentType &&
      ['application/x-octet-stream', 'application/zip'].some((type) => contentType.includes(type))
    ) {
      return res.blob();
    }

    return res.text();
  };
};
